import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axiosInstance from "../components/api/axiosInstance";
import PortalAlert from "./PortalAlert";
import PortalFormPage from "./PortalFormPage";
import { mobileValidationMessage, sanitizeMobileInput } from "./portalPhone";
import { capturesAllBusinessLeads } from "./portalSession";
import { BUSINESS_MODEL_OPTIONS, leadFieldProfile, projectOptionLabel } from "../utils/leadProfile";
import PortalSelect from "./PortalSelect";

const EMPTY = {
  Domain_Id: "",
  Business_Model: "",
  Tenant_Client_Id: "",
  Project_Id: "",
  Service_Id: "",
  First_Name: "",
  Last_Name: "",
  Country_Code: "+91",
  Mobile_Number: "",
  Email: "",
  Property_Service: "",
  Property_Service_Link: "",
  Tenant_Project_Name: "",
  Property_Location: "",
  Requirement_Type: "",
  Sharing_Preference: "",
  Move_In_Date: "",
  Property_Type_Id: "",
  Budget_Min: "",
  Budget_Max: "",
  Preferred_Location: "",
  Source_Id: "",
  Lead_Status_Id: "",
  Site_Visit: false,
  Notes: "",
};

function isActive(row) {
  return String(row?.Status || row?.status || "Active").toLowerCase() !== "inactive";
}

function RegisterLead() {
  const navigate = useNavigate();
  const [form, setForm] = useState(EMPTY);
  const [propertyTypes, setPropertyTypes] = useState([]);
  const [tenantClients, setTenantClients] = useState([]);
  const [clientProjects, setClientProjects] = useState([]);
  const [clientServices, setClientServices] = useState([]);
  const [loadingClients, setLoadingClients] = useState(false);
  const [loadingAssets, setLoadingAssets] = useState(false);
  const [requirementTypes, setRequirementTypes] = useState(["Buy", "Rent", "Lease", "Accommodation"]);
  const [sources, setSources] = useState([]);
  const [leadStatuses, setLeadStatuses] = useState([]);
  const [countries, setCountries] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [businessTypes, setBusinessTypes] = useState([]);
  const [allBusinessLeads, setAllBusinessLeads] = useState(() => capturesAllBusinessLeads());
  const [tenantModel, setTenantModel] = useState("");
  const [tenantBusinessType, setTenantBusinessType] = useState("");
  const [tenantServices, setTenantServices] = useState([]);

  const selectedBusinessType = businessTypes.find((d) => String(d.Domain_Id) === String(form.Domain_Id));
  const model = allBusinessLeads ? form.Business_Model : form.Business_Model || tenantModel;
  const profile = leadFieldProfile(
    allBusinessLeads ? selectedBusinessType?.Domain_Name : tenantBusinessType,
    model
  );
  // The model picker is only needed when the tenant's own model is unknown or leads span other businesses.
  const showModelSelect = allBusinessLeads || !tenantModel;
  // Service tenants pick from all their services; the brand/branch comes from the chosen service.
  const serviceMode = !allBusinessLeads && profile.service;
  const serviceOptions = useMemo(() => {
    const multipleOwners = new Set(tenantServices.map((s) => String(s.clientId))).size > 1;
    return tenantServices.map((s) => ({
      ...s,
      label: multipleOwners && s.clientName ? `${s.name} — ${s.clientName}` : s.name,
    }));
  }, [tenantServices]);
  const useProjectMode = !loadingAssets && profile.project && clientProjects.length > 0;
  const useServiceMode = !loadingAssets && profile.service && clientServices.length > 0;
  const offeringWord = profile.project ? "projects" : profile.service ? "services" : "projects or services";

  const phoneCountryOptions = useMemo(() => {
    const mapped = (countries || [])
      .map((c) => ({
        id: c.country_id ?? c.Country_Id ?? c.id,
        code: String(c.country_code || c.country_short_code || c.Country_Code || "").trim(),
        name: c.country_name || c.Country_Name || c.name || "",
      }))
      .filter((x) => x.code);
    const codes = new Set(["+91"]);
    mapped.forEach((c) => {
      const code = c.code.startsWith("+") ? c.code : `+${c.code}`;
      codes.add(code);
    });
    return Array.from(codes).sort((a, b) => a.localeCompare(b));
  }, [countries]);

  useEffect(() => {
    let active = true;
    (async () => {
      setLoadingClients(true);
      try {
        const [metaRes, countriesRes, clientsRes, servicesRes] = await Promise.all([
          axiosInstance.get("/client/lead-meta"),
          axiosInstance.get("/countries").catch(() => ({ data: [] })),
          axiosInstance.get("/client/tenant-clients").catch(() => ({ data: { data: [] } })),
          axiosInstance.get("/client/tenant-services").catch(() => ({ data: { data: [] } })),
        ]);
        if (!active) return;
        const rawServices = Array.isArray(servicesRes.data?.data) ? servicesRes.data.data : [];
        setTenantServices(
          rawServices
            .filter(isActive)
            .map((s) => ({
              id: s.Tenant_Client_Service_Id,
              name: s.Service_Name || `Service #${s.Tenant_Client_Service_Id}`,
              clientId: s.Tenant_Client_Id,
              clientName: s.Client_Name || "",
            }))
            .filter((s) => s.id != null)
        );
        const data = metaRes.data?.data || {};
        setPropertyTypes(Array.isArray(data.propertyTypes) ? data.propertyTypes : []);
        setSources(Array.isArray(data.sources) ? data.sources : []);
        const statuses = Array.isArray(data.leadStatuses) ? data.leadStatuses : [];
        setLeadStatuses(statuses);
        const newStatus = statuses.find(
          (s) => String(s.Status || s.status || "").trim().toLowerCase() === "new"
        );
        if (newStatus?.Status_Id != null) {
          setForm((f) => ({
            ...f,
            Lead_Status_Id: f.Lead_Status_Id || String(newStatus.Status_Id),
          }));
        }
        if (Array.isArray(data.requirementTypes) && data.requirementTypes.length) {
          setRequirementTypes(data.requirementTypes);
        }
        setBusinessTypes(Array.isArray(data.businessTypes) ? data.businessTypes : []);
        if (typeof data.capturesAllBusinessLeads === "boolean") {
          setAllBusinessLeads(data.capturesAllBusinessLeads);
        }
        setTenantModel(data.businessModel || "");
        setTenantBusinessType(data.businessType || "");
        const rawCountries = Array.isArray(countriesRes?.data)
          ? countriesRes.data
          : Array.isArray(countriesRes?.data?.data)
            ? countriesRes.data.data
            : [];
        setCountries(rawCountries);
        const rawClients = Array.isArray(clientsRes.data?.data)
          ? clientsRes.data.data
          : Array.isArray(clientsRes.data)
            ? clientsRes.data
            : [];
        setTenantClients(
          rawClients
            .filter(isActive)
            .map((c) => ({
              id: c.Tenant_Client_Id,
              name: c.Client_Name || `Client #${c.Tenant_Client_Id}`,
            }))
            .filter((c) => c.id != null)
        );
      } catch (_) {
        // Meta is optional; form still works with free-text fields.
      } finally {
        if (active) setLoadingClients(false);
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    let active = true;
    const clientId = form.Tenant_Client_Id;
    if (!clientId) {
      setClientProjects([]);
      setClientServices([]);
      return undefined;
    }
    setLoadingAssets(true);
    (async () => {
      try {
        const [projectsRes, servicesRes] = await Promise.all([
          axiosInstance.get(`/client/tenant-clients/${clientId}/projects`),
          axiosInstance.get(`/client/tenant-clients/${clientId}/services`),
        ]);
        if (!active) return;
        const projectsRaw = Array.isArray(projectsRes.data?.data) ? projectsRes.data.data : [];
        const servicesRaw = Array.isArray(servicesRes.data?.data) ? servicesRes.data.data : [];
        setClientProjects(
          projectsRaw
            .filter(isActive)
            .map((p) => ({
              id: p.Tenant_Project_Id,
              name: p.Project_Name || `Project #${p.Tenant_Project_Id}`,
              label: projectOptionLabel(p.Project_Name || `Project #${p.Tenant_Project_Id}`, p.Address),
            }))
            .filter((p) => p.id != null)
        );
        setClientServices(
          servicesRaw
            .filter(isActive)
            .map((s) => ({
              id: s.Tenant_Client_Service_Id,
              name: s.Service_Name || `Service #${s.Tenant_Client_Service_Id}`,
            }))
            .filter((s) => s.id != null)
        );
      } catch (_) {
        if (active) {
          setClientProjects([]);
          setClientServices([]);
        }
      } finally {
        if (active) setLoadingAssets(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [form.Tenant_Client_Id]);

  const setField = (name, value) => setForm((f) => ({ ...f, [name]: value }));

  const resetOffering = {
    Project_Id: "",
    Service_Id: "",
    Property_Service: "",
    Property_Service_Link: "",
    Tenant_Project_Name: "",
    Sharing_Preference: "",
    Move_In_Date: "",
    Property_Type_Id: "",
    Property_Location: "",
    Requirement_Type: "",
  };

  const handleSelectBusinessType = (domainId) => {
    const type = businessTypes.find((d) => String(d.Domain_Id) === String(domainId));
    setForm((f) => ({
      ...f,
      ...resetOffering,
      Domain_Id: domainId,
      Business_Model: type?.Default_Business_Model || "",
    }));
  };

  const handleSelectModel = (value) => setForm((f) => ({ ...f, ...resetOffering, Business_Model: value }));
  const ownerRequired = !allBusinessLeads && !serviceMode && tenantClients.length > 0;

  const handleSelectTenantService = (serviceId) => {
    const service = tenantServices.find((s) => String(s.id) === String(serviceId));
    setForm((f) => ({
      ...f,
      Tenant_Client_Id: service?.clientId != null ? String(service.clientId) : "",
      Service_Id: serviceId,
      Project_Id: "",
      Tenant_Project_Name: "",
      Property_Service: service?.name || "",
    }));
  };

  useEffect(() => {
    if (serviceMode && tenantServices.length === 1 && !form.Service_Id) {
      handleSelectTenantService(String(tenantServices[0].id));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serviceMode, tenantServices, form.Service_Id]);

  const handleSelectClient = (clientId) => {
    setForm((f) => ({
      ...f,
      Tenant_Client_Id: clientId,
      Project_Id: "",
      Service_Id: "",
      Property_Service: "",
      Tenant_Project_Name: "",
      Sharing_Preference: "",
      Move_In_Date: "",
    }));
  };

  const handleSelectProject = (projectId) => {
    const project = clientProjects.find((p) => String(p.id) === String(projectId));
    const name = project?.name || "";
    setForm((f) => ({
      ...f,
      Project_Id: projectId,
      Service_Id: "",
      Tenant_Project_Name: name,
      Property_Service: name,
      ...(projectId ? {} : { Sharing_Preference: "", Move_In_Date: "" }),
    }));
  };

  const handleSelectService = (serviceId) => {
    const service = clientServices.find((s) => String(s.id) === String(serviceId));
    const name = service?.name || "";
    setForm((f) => ({
      ...f,
      Service_Id: serviceId,
      Project_Id: "",
      Tenant_Project_Name: "",
      Property_Service: name,
      Sharing_Preference: "",
      Move_In_Date: "",
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    if (allBusinessLeads && !form.Domain_Id) {
      setError("Business type is required.");
      return;
    }
    if (!profile.model) {
      setError("Business model (Project Based or Service Based) is required.");
      return;
    }
    if (serviceMode && tenantServices.length && !form.Service_Id) {
      setError("Service is required.");
      return;
    }
    if (ownerRequired && !form.Tenant_Client_Id) {
      setError(`${profile.ownerLabel} is required.`);
      return;
    }
    if (form.Tenant_Client_Id) {
      if (useProjectMode && !form.Project_Id) {
        setError("Project is required.");
        return;
      }
      if (useServiceMode && !form.Service_Id) {
        setError("Service is required.");
        return;
      }
    }
    if (!form.First_Name.trim()) {
      setError("First name is required.");
      return;
    }
    const mobileError = mobileValidationMessage(form.Mobile_Number, { required: true });
    if (mobileError) {
      setError(mobileError);
      return;
    }
    if (!form.Lead_Status_Id) {
      setError("Status is required.");
      return;
    }

    const countryCode = (() => {
      const raw = String(form.Country_Code || "+91").trim();
      if (!raw) return "+91";
      return raw.startsWith("+") ? raw : `+${raw}`;
    })();

    const payload = {
      Domain_Id: allBusinessLeads && form.Domain_Id ? Number(form.Domain_Id) : null,
      Business_Model: profile.model || null,
      First_Name: form.First_Name.trim(),
      Last_Name: form.Last_Name.trim() || null,
      Country_Code: countryCode,
      Mobile_Number: form.Mobile_Number.trim(),
      Email: form.Email.trim() || null,
      Property_Service: form.Property_Service.trim() || null,
      Tenant_Project_Name: form.Tenant_Project_Name.trim() || null,
      Tenant_Project_Id: useProjectMode && form.Project_Id ? Number(form.Project_Id) : null,
      Property_Location: profile.showPropertyDetails ? form.Property_Location.trim() || null : null,
      Requirement_Type: profile.showRequirementType ? form.Requirement_Type || null : null,
      Sharing_Preference: profile.showPropertyDetails ? form.Sharing_Preference.trim() || null : null,
      Move_In_Date: profile.showPropertyDetails || profile.showPreferredDate ? form.Move_In_Date || null : null,
      Property_Type_Id: profile.showPropertyDetails ? form.Property_Type_Id || null : null,
      Budget_Min: profile.showBudget && form.Budget_Min !== "" ? form.Budget_Min : null,
      Budget_Max: profile.showBudget && form.Budget_Max !== "" ? form.Budget_Max : null,
      Preferred_Location: form.Preferred_Location.trim() || null,
      Source_Id: form.Source_Id || null,
      Lead_Status_Id: form.Lead_Status_Id ? Number(form.Lead_Status_Id) : null,
      Site_Visit: profile.showSiteVisit && !!form.Site_Visit,
      Notes: form.Notes.trim() || null,
    };

    setSaving(true);
    try {
      await axiosInstance.post("/client/leads", payload);
      setSuccess("Lead added successfully.");
      setTimeout(() => navigate("/portal/leads"), 700);
    } catch (err) {
      setError(err.response?.data?.message || err.response?.data?.error || "Could not add lead.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <PortalFormPage
      title="Add Lead"
      backLabel="Back to leads"
      onBack={() => navigate("/portal/leads")}
      onSubmit={handleSubmit}
      submitting={saving}
      submitLabel="Save lead"
      className="cportal-lead-form cportal-form-page--two-col"
    >
        <PortalAlert type="error" message={error} onClear={() => setError("")} />
        <PortalAlert type="success" message={success} onClear={() => setSuccess("")} />

        <h2 className="cportal-form-section">Lead Contact</h2>
        <div className="cportal-form-grid">
          <div className="cportal-field">
            <label>
              First name <span className="cportal-required">*</span>
            </label>
            <input value={form.First_Name} onChange={(e) => setField("First_Name", e.target.value)} required />
          </div>
          <div className="cportal-field">
            <label>Last name</label>
            <input value={form.Last_Name} onChange={(e) => setField("Last_Name", e.target.value)} />
          </div>
          <div className="cportal-field cportal-field-phone">
            <label>
              Mobile number <span className="cportal-required">*</span>
            </label>
            <div className="cportal-phone-row">
              <PortalSelect
                value={form.Country_Code}
                onChange={(e) => setField("Country_Code", e.target.value)}
                aria-label="Country code"
              >
                {phoneCountryOptions.map((code) => (
                  <option key={code} value={code}>
                    {code}
                  </option>
                ))}
              </PortalSelect>
              <input
                value={form.Mobile_Number}
                onChange={(e) => setField("Mobile_Number", sanitizeMobileInput(e.target.value))}
                inputMode="numeric"
                maxLength={10}
                placeholder="Mobile number"
                required
              />
            </div>
          </div>
          <div className="cportal-field">
            <label>Email</label>
            <input
              type="email"
              value={form.Email}
              onChange={(e) => setField("Email", e.target.value)}
              placeholder="Email address"
            />
          </div>
          <div className="cportal-field">
            <label>Lead Location</label>
            <input value={form.Preferred_Location} onChange={(e) => setField("Preferred_Location", e.target.value)} />
          </div>
          {allBusinessLeads ? (
            <div className="cportal-field">
              <label>
                Business type <span className="cportal-required">*</span>
              </label>
              <PortalSelect value={form.Domain_Id} onChange={(e) => handleSelectBusinessType(e.target.value)} required>
                <option value="">Select business type…</option>
                {businessTypes.map((d) => (
                  <option key={d.Domain_Id} value={d.Domain_Id}>
                    {d.Domain_Name}
                  </option>
                ))}
              </PortalSelect>
            </div>
          ) : null}
        </div>

        <h2 className="cportal-form-section">
          {profile.project ? "Project" : profile.service ? "Service" : "Business Model"}
        </h2>
        <div className="cportal-form-grid cportal-form-grid--3">
          {showModelSelect ? (
            <div className="cportal-field">
              <label>
                Business Model <span className="cportal-required">*</span>
              </label>
              <PortalSelect
                value={model}
                onChange={(e) => handleSelectModel(e.target.value)}
                disabled={allBusinessLeads && !form.Domain_Id}
                required
              >
                <option value="">{allBusinessLeads && !form.Domain_Id ? "Select business type first" : "Select model…"}</option>
                {BUSINESS_MODEL_OPTIONS.map((m) => (
                  <option key={m.value} value={m.value}>
                    {m.label}
                  </option>
                ))}
              </PortalSelect>
            </div>
          ) : null}

          {serviceMode ? (
            serviceOptions.length ? (
              <div className="cportal-field">
                <label>
                  Service <span className="cportal-required">*</span>
                </label>
                <PortalSelect
                  value={form.Service_Id}
                  onChange={(e) => handleSelectTenantService(e.target.value)}
                  required
                >
                  <option value="">Select service…</option>
                  {serviceOptions.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.label}
                    </option>
                  ))}
                </PortalSelect>
              </div>
            ) : (
              <div className="cportal-field">
                <label>Service / Interest</label>
                <input
                  value={form.Property_Service}
                  onChange={(e) => setField("Property_Service", e.target.value)}
                  placeholder="Service the lead asked about"
                />
              </div>
            )
          ) : (
          <>
          <div className="cportal-field">
            <label>
              {profile.ownerLabel} {ownerRequired ? <span className="cportal-required">*</span> : null}
            </label>
            <PortalSelect
              value={form.Tenant_Client_Id}
              onChange={(e) => handleSelectClient(e.target.value)}
              disabled={loadingClients || !tenantClients.length}
              required={ownerRequired}
            >
              <option value="">
                {loadingClients
                  ? "Loading…"
                  : allBusinessLeads
                    ? "None (new prospect)"
                    : tenantClients.length
                      ? `Select ${profile.ownerLabel.toLowerCase()}…`
                      : "None available"}
              </option>
              {tenantClients.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </PortalSelect>
          </div>

          {!profile.model ? (
            <div className="cportal-field">
              <label>{profile.offeringLabel}</label>
              <PortalSelect disabled value="">
                <option value="">Select business model first</option>
              </PortalSelect>
            </div>
          ) : form.Tenant_Client_Id ? (
            loadingAssets ? (
              <div className="cportal-field">
                <label>{profile.offeringLabel}</label>
                <PortalSelect disabled value="">
                  <option value="">Loading…</option>
                </PortalSelect>
              </div>
            ) : useProjectMode ? (
              <div className="cportal-field">
                <label>
                  Project <span className="cportal-required">*</span>
                </label>
                <PortalSelect
                  value={form.Project_Id}
                  onChange={(e) => handleSelectProject(e.target.value)}
                  required
                >
                  <option value="">Select project…</option>
                  {clientProjects.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.label || p.name}
                    </option>
                  ))}
                </PortalSelect>
              </div>
            ) : useServiceMode ? (
              <div className="cportal-field">
                <label>
                  Service <span className="cportal-required">*</span>
                </label>
                <PortalSelect
                  value={form.Service_Id}
                  onChange={(e) => handleSelectService(e.target.value)}
                  required
                >
                  <option value="">Select service…</option>
                  {clientServices.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                    </option>
                  ))}
                </PortalSelect>
              </div>
            ) : (
              <div className="cportal-field">
                <label>{profile.offeringLabel}</label>
                <PortalSelect disabled value="">
                  <option value="">No {offeringWord} added yet</option>
                </PortalSelect>
              </div>
            )
          ) : allBusinessLeads || !tenantClients.length ? (
            <div className="cportal-field">
              <label>{profile.offeringLabel} / Interest</label>
              <input
                value={form.Property_Service}
                onChange={(e) => setField("Property_Service", e.target.value)}
                placeholder={profile.project ? "Project name the lead asked about" : "Service the lead asked about"}
              />
            </div>
          ) : (
            <div className="cportal-field">
              <label>{profile.offeringLabel}</label>
              <PortalSelect disabled value="">
                <option value="">Select {profile.ownerLabel.toLowerCase()} first</option>
              </PortalSelect>
            </div>
          )}
          </>
          )}
        </div>

        {!profile.showDetails || !profile.model ? null : (
        <>
        {serviceMode && !profile.showPropertyDetails ? null : (
          <h2 className="cportal-form-section">{profile.detailsTitle}</h2>
        )}
        <div
          className={`cportal-form-grid${
            serviceMode && !profile.showPropertyDetails ? " cportal-form-grid--spaced-top" : ""
          }`}
        >
          {profile.showPropertyDetails ? (
            <div className="cportal-field">
              <label>Property location</label>
              <input value={form.Property_Location} onChange={(e) => setField("Property_Location", e.target.value)} />
            </div>
          ) : null}
          {profile.showRequirementType ? (
            <div className="cportal-field">
              <label>Requirement type</label>
              <PortalSelect value={form.Requirement_Type} onChange={(e) => setField("Requirement_Type", e.target.value)}>
                <option value="">Select…</option>
                {requirementTypes.map((r) => (
                  <option key={r} value={r}>
                    {r}
                  </option>
                ))}
              </PortalSelect>
            </div>
          ) : null}
          {profile.showPropertyDetails ? (
            <>
              <div className="cportal-field">
                <label>Property type</label>
                <PortalSelect value={form.Property_Type_Id} onChange={(e) => setField("Property_Type_Id", e.target.value)}>
                  <option value="">Select…</option>
                  {propertyTypes.map((p) => (
                    <option key={p.Property_Type_Id} value={p.Property_Type_Id}>
                      {p.Property_Type_Name}
                    </option>
                  ))}
                </PortalSelect>
              </div>
              <div className="cportal-field">
                <label>Sharing preference</label>
                <input
                  type="text"
                  value={form.Sharing_Preference}
                  onChange={(e) => setField("Sharing_Preference", e.target.value)}
                  placeholder="e.g. 2 sharing, 2 BHK"
                  maxLength={100}
                />
              </div>
            </>
          ) : null}
          {profile.showPropertyDetails || profile.showPreferredDate ? (
            <div className="cportal-field">
              <label>{profile.showPropertyDetails ? "Move-in date" : "Preferred date"}</label>
              <input
                type="date"
                value={form.Move_In_Date}
                onChange={(e) => setField("Move_In_Date", e.target.value)}
              />
            </div>
          ) : null}
          {profile.showBudget ? (
            <>
              <div className="cportal-field">
                <label>Budget min</label>
                <input
                  type="number"
                  value={form.Budget_Min}
                  onChange={(e) => setField("Budget_Min", e.target.value)}
                />
              </div>
              <div className="cportal-field">
                <label>Budget max</label>
                <input
                  type="number"
                  value={form.Budget_Max}
                  onChange={(e) => setField("Budget_Max", e.target.value)}
                />
              </div>
            </>
          ) : null}
        </div>
        </>
        )}

        <h2 className="cportal-form-section">Lead Pipeline</h2>
        <div className="cportal-form-grid">
          <div className="cportal-field">
            <label>
              Status <span className="cportal-required">*</span>
            </label>
            <PortalSelect
              value={form.Lead_Status_Id}
              onChange={(e) => setField("Lead_Status_Id", e.target.value)}
              required
            >
              <option value="">Select…</option>
              {leadStatuses.map((s) => (
                <option key={s.Status_Id} value={s.Status_Id}>
                  {s.Status}
                </option>
              ))}
            </PortalSelect>
          </div>
          <div className="cportal-field">
            <label>Source</label>
            <PortalSelect value={form.Source_Id} onChange={(e) => setField("Source_Id", e.target.value)}>
              <option value="">Select…</option>
              {sources.map((s) => (
                <option key={s.Source_Id} value={s.Source_Id}>
                  {s.Source_Name}
                </option>
              ))}
            </PortalSelect>
          </div>
          {profile.showSiteVisit ? (
            <div className="cportal-field cportal-field-check">
              <label>
                <input
                  type="checkbox"
                  checked={form.Site_Visit}
                  onChange={(e) => setField("Site_Visit", e.target.checked)}
                />
                <span>Site visit</span>
              </label>
            </div>
          ) : null}
        </div>

        <h2 className="cportal-form-section">Notes</h2>
        <div className="cportal-field">
          <textarea rows={3} value={form.Notes} onChange={(e) => setField("Notes", e.target.value)} />
        </div>
    </PortalFormPage>
  );
}

export default RegisterLead;
